import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const callStackCode = `// Visualizing Function Execution on the Call Stack

function multiply(a, b) {
    return a * b;
}

function square(n) {
    // 1. multiply(n, n) is pushed to the stack
    return multiply(n, n);
}

function printSquare(n) {
    // 2. square(n) is pushed to the stack
    const result = square(n);
    console.log(result);
}

// 3. printSquare(4) is the entry point
printSquare(4);

// Execution order (Last-In-First-Out):
// [printSquare] -&gt; [square] -&gt; [multiply]
// multiply returns -&gt; square returns -&gt; printSquare returns`;

const eventLoopBasicsCode = `// The classic Event Loop demonstration

console.log('Script Start');

// Scheduled as a Macrotask
setTimeout(() => {
    console.log('Macrotask: setTimeout callback');
}, 0);

// Scheduled as a Microtask (Priority!)
Promise.resolve().then(() => {
    console.log('Microtask: Promise resolved');
});

console.log('Script End');

/* 
Expected Output Order:
1. Script Start
2. Script End
3. Microtask: Promise resolved
4. Macrotask: setTimeout callback
*/`;

const microtaskStarvationCode = `// CAUTION: Demonstrating Microtask Starvation (Infinite Loop)

function starveEventLoop() {
    // Scheduling a microtask that schedules another microtask
    Promise.resolve().then(starveEventLoop);
}

// If executed, the code below would never run because 
// the Microtask Queue is never drained!
// setTimeout(() => console.log("I will never run"), 0);

// Correct way to "breath" using a Macrotask
function healthyLoop() {
    console.log("Processing...");
    setTimeout(healthyLoop, 0); 
    // Macrotask allows the UI to repaint between cycles
}`;

const complexExecutionCode = `// Multi-queue orchestration challenge

console.log('A');

setTimeout(() => {
    console.log('B'); // Macrotask 1
}, 0);

Promise.resolve().then(() => {
    console.log('C'); // Microtask 1
    setTimeout(() => console.log('D'), 0); // Macrotask 2 (added during C)
});

Promise.resolve().then(() => {
    console.log('E'); // Microtask 2
});

console.log('F');

// Logical flow:
// 1. Sync: A, F
// 2. Clear All Microtasks: C, E
// 3. First Macrotask: B
// 4. Next Macrotask: D
// Output: A, F, C, E, B, D`;

const JSEventLoop = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Events & Concurrency</div>
                <h1>The Event Loop: JavaScript's Engine Room</h1>
                <p className="hero-lead">
                    Deep dive into the mechanics of the Call Stack, Task Queues, and the Microtask Queue.
                    Understand how JavaScript manages concurrency on a single thread and why the event
                    loop is the secret to high-performance, non-blocking applications.
                </p>
            </div>

            <div className="content-section">
                <h2>The Call Stack: Tracking Execution Contexts</h2>
                <p>
                    At its core, JavaScript is a single-threaded language, meaning it has exactly one **Call Stack**.
                    The Call Stack is a synchronous data structure that follows the "Last-In-First-Out" (LIFO)
                    principle to manage function execution. When you call a function, a new "Execution Context"
                    is created and pushed onto the top of the stack. This context contains the function's
                    arguments, local variables, and the specific place in the code currently being executed.
                    When the function reaches a `return` statement or the end of its block, its context is
                    popped off the stack, and the engine resumes execution in the underlying context. This
                    linear flow is what makes JavaScript code predictablebut it also means that if a
                    function takes a long time to finish, it "blocks" the stack, preventing any other code
                    from running.
                </p>
                <p>
                    Understanding the Call Stack is vital for debugging "Stack Overflow" errors, which occur
                    when a function calls itself recursively without a proper exit condition. Each recursive
                    call adds a new frame to the stack, and browsers have a finite memory limit for how large
                    this stack can grow. Once that limit is hit, the engine throws a `RangeError`, terminating
                    execution to protect the host system from crashing. This reveals the trade-off inherent
                    in recursive patterns: while they provide elegant solutions for tree-like data structures,
                    they risk exhausting the primary execution thread's memory. In modern development,
                    optimizing stack usage involves identifying deep recursive paths and converting them
                    into iterative loops or leveraging "Tail Call Optimization" where supported. By mastering
                    stack dynamics, you gain control over the most fundamental aspect of program execution.
                </p>

                <CodeBlock code={callStackCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Event Loop & Web APIs: Multi-threaded Environments</h2>
                <p>
                    While the JavaScript engine (like V8) is single-threaded, the **Environment** (the Browser
                    or Node.js) is not. Browsers provide powerful "Web APIs"such as `setTimeout`, `fetch`,
                    and DOM listenersthat run in separate threads managed by the browser's C++ core. When
                    you call `setTimeout`, you aren't waiting in the JavaScript thread; you are asking the
                    Browser to set a timer on its own thread. Once that timer expires, the Browser doesn't
                    just "jump" into the Call Stack; it places the callback into a **Task Queue**. This
                    orchestration is the primary reason why JavaScript can be "non-blocking" while only
                    being able to do one thing at a time. The engine delegates heavy lifting to the
                    surrounding environment, keeping the main thread free for high-frequency logic.
                </p>
                <p>
                    The **Event Loop** is the continuous process that monitors both the Call Stack and the
                    Task Queues. Its job is simple but critical: if the Call Stack is empty, it takes the
                    first task from the queue and pushes it onto the stack to be executed. This check
                    happens at a blistering speed, often thousands of times per second. However, the requirement
                    that the Call Stack must be empty is non-negotiable. If you run a heavy loop for 10 seconds,
                    the event loop is effectively frozen; even if your timer expired 9.9 seconds ago, its
                    callback will wait until the stack finally clears. This explains why `setTimeout(fn, 0)`
                    doesn't actually mean "run in zero milliseconds"; it means "run as soon as the current
                    synchronous work is finished and the event loop can reach the task."
                </p>

                <CodeBlock code={eventLoopBasicsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Microtasks vs Tasks: The Priority Battle</h2>
                <p>
                    Modern JavaScript introduced a second, higher-priority queue: the **Microtask Queue**
                    (often called the Job Queue). This queue is dedicated specifically to Promise resolutions
                    (via `.then`, `.catch`, `.finally`) and the `queueMicrotask` API. The Event Loop
                    processes these differently than standard Tasks (Macrotasks). After the Call Stack is
                    cleared of synchronous code, the engine **drains the entire Microtask Queue** before
                    moving on to the next Macrotask. If a microtask adds another microtask, the engine will
                    keep processing them until the queue is completely empty. This design ensures that
                    asynchronous state changes (like data arriving for a React component) happen as
                    quickly as possible, ensuring that the application state remains consistent before the
                    browser attempts to repaint the screen.
                </p>
                <p>
                    This prioritization leads to the phenomenon of **Microtask Starvation**. Because the
                    Event Loop will not move to the next Macrotask until the Microtask Queue is empty,
                    it is possible to "starve" the rest of the application by recursively scheduling
                    microtasks. During starvation, the browser cannot process input events, run timers,
                    or perform UI repaints, resulting in a completely frozen interface. This is why
                    Promises are significantly more "aggressive" than `setTimeout`. Understanding the
                    difference is crucial for performance profiling; where 100 `setTimeout` calls might
                    smoothly interleave with user interactions, 100 recursive Promise resolutions will
                    effectively hijack the main thread. As an engineer, choosing the right queue for the
                    right task is a fundamental skill for building responsive user interfaces.
                </p>

                <CodeBlock code={microtaskStarvationCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Rendering Pipeline and Performance</h2>
                <p>
                    The Event Loop's complexity increases when we consider the **Rendering Pipeline**. In a
                    typical browser, the engine aims to repaint the screen every 16.7 milliseconds (to
                    maintain 60 Frames Per Second). The Event Loop includes a "Render" phase that happens
                    between Macrotasks, but only if the hardware and the loop timing allow for it. If a Macrotask
                    takes too longsay, 50msthree potential frames have been skipped, leading to
                    "jank" or stuttering in animations. To solve this, browsers provide `requestAnimationFrame`,
                    a specialized hook that ensures a callback runs specifically before the next repaint.
                    This API is much more efficient than using `setInterval` for animations because it
                    synchronizes with the hardware's refresh rate, ensuring zero wasted work.
                </p>
                <p>
                    For truly heavy computational worklike image processing, data parsing, or complex
                    mathematicswe must move the work off the main thread entirely using **Web Workers**.
                    Web Workers run in their own dedicated thread with their own Call Stack and Event Loop.
                    They communicate with the main thread via message passing, ensuring that the primary
                    thread remains free to handle 60fps animations and instant user feedback. This
                    architecture is common in modern web applications that process large datasets or
                    perform AI-related tasks locally. By delegating the "heavy lifting" to a worker,
                    you preserve the integrity of the main Event Loop, maintaining a premium, "instant"
                    feel for the end-user. Mastering the interplay between these various threads and
                    queues is what separates a JavaScript developer from a high-performance Front-end Engineer.
                </p>

                <CodeBlock code={complexExecutionCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Engineering Best Practices</h2>
                <p>
                    Always keep the main thread "short and sweet" by breaking down long-running synchronous
                    tasks into smaller chunks. Use `setTimeout(chunk, 0)` or `requestIdleCallback` to
                    manually yield control back to the Event Loop, allowing the browser to process high-priority
                    user events in between your processing steps. Prefer Promises and `async/await` for
                    data-dependent state updates, but be conscious of the Microtask priority to avoid starvation.
                    If an operation takes longer than 50-100ms, it is an immediate candidate for a Web Worker.
                    Regularly audit your application using the browser's "Performance" tab to visualize long
                    tasks and identify blocks in the Render Pipeline. By respecting the single-threaded nature
                    of the engine, you create software that feels fluid, reliable, and professional.
                </p>

                <KeyFacts><ul>
                        <li>**Call Stack:** Strictly LIFO management of synchronous execution contexts.</li>
                        <li>**Web APIs:** Browser threads that handle asynchronous heavy lifting.</li>
                        <li>**Priority:** Microtasks (Promises) always execute before the next Macrotask.</li>
                        <li>**Consistency:** Engine drains the entire Microtask queue in one go.</li>
                        <li>**Performance:** Long tasks block the Render phase, causing visible "jank."</li>
                        <li>**Optimization:** Use `requestAnimationFrame` for anything visual.</li>
                        <li>**Isolation:** Move long-running logic to Web Workers for multi-threaded performance.</li>
                    </ul></KeyFacts>
            </div>

        </div>
    );
};

export default JSEventLoop;


